import React, { useEffect, useState, useRef } from "react";
import PageModal from "../Components/PageModal";
import BillingForm from "./BillingForm";
import {
  getAllDocsSnap,
  addNewDoc,
} from "../../../utils/firebase/Firebase.utils";
import BillingCard from "./BillingCard";
import { slicedData } from "../../../utils/EleghoUtils";
import { DataGrid } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import BillingPageDetails from "./BillingPageDetails";
import toast from "react-hot-toast";
import {
  FiDollarSign,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiPlusCircle,
  FiCalendar,
  FiDroplet,
  FiBell,
  FiXCircle,
  FiUpload,
  FiFileText,
  FiDownload,
  FiFile,
} from "react-icons/fi";
import { useSelector } from "react-redux";

export default function Billing() {
  const [addMode, setAddMode] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [billings, setBillings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [numberOfItemsPerPage, setNumberOfItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState(false);
  const [selectedForView, setSelectedForView] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const fileInputRef = useRef(null);
  const currentUser = useSelector((state) => state.userObject);

  const columns = [
    { field: "identifier", headerName: "Identifier", width: 150 },
    { field: "name", headerName: "Name", width: 200 },
    { field: "billingType", headerName: "Billing Type", width: 150 },
    {
      field: "amount",
      headerName: "Amount",
      width: 130,
      renderCell: (params) => {
        const amount = params.value || 0;
        return `₱${amount.toLocaleString()}`;
      },
    },
    { field: "description", headerName: "Description", width: 200 },
    {
      field: "dueDate",
      headerName: "Due Date",
      width: 130,
    },
    {
      field: "status",
      headerName: "Status",
      width: 200,
      renderCell: (params) => {
        const statusConfig = {
          paid: {
            text: "text-green-600",
            icon: <FiCheckCircle className="w-4 h-4 text-green-600" />,
          },
          pending: {
            text: "text-blue-600",
            icon: <FiClock className="w-4 h-4 text-blue-600" />,
          },
          overdue: {
            text: "text-red-600",
            icon: <FiAlertCircle className="w-4 h-4 text-red-600" />,
          },
          unpaid: {
            text: "text-orange-600",
            icon: <FiXCircle className="w-4 h-4 text-orange-600" />,
          },
          "deposit slip uploaded": {
            text: "text-purple-600",
            icon: <FiFileText className="w-4 h-4 text-purple-600" />,
          },
        };

        const config = statusConfig[params.value] || {
          text: "text-gray-600",
          icon: null,
        };

        return (
          <div
            className={`px-3 py-1.5 capitalize ${config.text} flex items-center gap-2 font-medium`}
          >
            {config.icon}
            <span>{params.value}</span>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    const fetchBillings = async () => {
      await getAllDocsSnap("billings", (docs) => {
        setBillings(docs);
        setIsLoading(false);
      });
    };
    fetchBillings();
  }, []);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const handleViewMode = (params) => {
    setViewMode(true);
    setSelectedForView(params.row);
  };

  const stats = {
    totalBillings: billings.length,
    pendingBillings: billings.filter((b) => b.status === "pending").length,
    paidBillings: billings.filter((b) => b.status === "paid").length,
    overdueBillings: billings.filter((b) => b.status === "overdue").length,
  };

  const handleExcelDownload = () => {
    // Convert billings data to CSV format
    const headers = [
      "Identifier",
      "Name",
      "Billing Type",
      "Amount",
      "Description",
      "Due Date",
      "Status",
    ];
    const csvData = billings.map((billing) => [
      billing.identifier,
      billing.name,
      billing.billingType,
      billing.amount,
      billing.description,
      new Date(billing.dueDate).toLocaleDateString(),
      billing.status,
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(","),
      ...csvData.map((row) => row.join(",")),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `billings_${new Date().toLocaleDateString()}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleTemplateDownload = () => {
    // Template headers based on billing field structure
    const headers = [
      "Identifier",
      "Name",
      "BillingType",
      "Amount",
      "SOANumber",
      "BillingDate",
      "DueDate",
      "Description",
    ];

    // Create sample row with empty values (optional)
    const sampleRow = [
      "UNT001",
      "Resident Name",
      "Electricity",
      "1000",
      "SOA-001",
      "2023-01-01",
      "2023-01-15",
      "Monthly electricity bill",
    ];

    // Create CSV content with headers and optional sample row
    const csvContent = [headers.join(","), sampleRow.join(",")].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `billing_template.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    console.log("File selected:", file);

    if (!file) {
      toast.error("No file selected");
      return;
    }

    // Check if it's a CSV file
    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      toast.error("Please upload a CSV file");
      return;
    }

    toast.loading("Processing CSV file...");

    // Reset states
    setCsvData([]);
    setValidationErrors([]);

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        console.log("File loaded successfully");
        toast.dismiss();

        const content = e.target.result;
        const lines = content.split("\n");

        if (lines.length <= 1) {
          toast.error("CSV file is empty or contains only headers");
          return;
        }

        // Validate header line
        const headers = lines[0].split(",").map((header) => header.trim());
        const requiredHeaders = [
          "Identifier",
          "Name",
          "BillingType",
          "Amount",
          "SOANumber",
          "BillingDate",
          "DueDate",
          "Description",
        ];

        // Check if all required headers are present
        const missingHeaders = requiredHeaders.filter(
          (header) => !headers.includes(header)
        );

        if (missingHeaders.length > 0) {
          setValidationErrors([
            `Missing required headers: ${missingHeaders.join(", ")}`,
          ]);
          toast.error("CSV file is missing required headers");
          setUploadMode(true);
          return;
        }

        // Parse and validate data
        const parsedData = [];
        const errors = [];

        // Start from index 1 to skip the header row
        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue; // Skip empty lines

          const values = lines[i].split(",").map((value) => value.trim());

          // Check if row has correct number of columns
          if (values.length !== headers.length) {
            errors.push(`Row ${i + 1}: Incorrect number of columns`);
            continue;
          }

          // Create object mapping headers to values
          const rowData = {};
          headers.forEach((header, index) => {
            rowData[header] = values[index];
          });

          // Validate required fields
          if (!rowData.Identifier) {
            errors.push(`Row ${i + 1}: Identifier is required`);
          }
          if (!rowData.BillingType) {
            errors.push(`Row ${i + 1}: BillingType is required`);
          }
          if (!rowData.Amount || isNaN(parseFloat(rowData.Amount))) {
            errors.push(`Row ${i + 1}: Amount must be a valid number`);
          }
          if (!rowData.DueDate || !isValidDate(rowData.DueDate)) {
            errors.push(
              `Row ${i + 1}: DueDate must be a valid date (YYYY-MM-DD)`
            );
          }
          if (!rowData.BillingDate || !isValidDate(rowData.BillingDate)) {
            errors.push(
              `Row ${i + 1}: BillingDate must be a valid date (YYYY-MM-DD)`
            );
          }

          // If no errors for this row, add to parsed data
          if (!errors.some((error) => error.includes(`Row ${i + 1}:`))) {
            parsedData.push(rowData);
          }
        }

        // Set validation errors if any
        if (errors.length > 0) {
          setValidationErrors(errors);
          toast.error(`Found ${errors.length} errors in the CSV file`);
        }

        // Set parsed data for preview
        setCsvData(parsedData);

        console.log("Parsed data:", parsedData);
        console.log("Validation errors:", errors);

        // Open upload modal if data is valid or if there are validation errors to show
        if (parsedData.length > 0 || errors.length > 0) {
          setUploadMode(true);
          if (parsedData.length > 0) {
            toast.success(`Found ${parsedData.length} valid billing records`);
          }
        } else {
          toast.error("No valid data found in the CSV file");
        }
      } catch (error) {
        console.error("Error processing CSV:", error);
        toast.error("Error processing CSV file: " + error.message);
      }
    };

    reader.onerror = (error) => {
      console.error("File read error:", error);
      toast.error("Error reading file");
    };

    try {
      reader.readAsText(file);
    } catch (error) {
      console.error("Error initiating file read:", error);
      toast.error("Error reading file: " + error.message);
    }

    // Reset file input so the same file can be selected again
    event.target.value = null;
  };

  const isValidDate = (dateString) => {
    // Check if date is in YYYY-MM-DD format
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) return false;

    // Check if date is valid
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
  };

  const handleSaveBillings = async () => {
    if (csvData.length === 0) {
      toast.error("No valid data to import");
      return;
    }

    setIsUploading(true);

    // Track successful and failed uploads
    let successCount = 0;
    let failedCount = 0;

    try {
      // Process each billing entry
      for (const billing of csvData) {
        try {
          // Format data for Firebase
          const billingData = {
            identifier: billing.Identifier,
            name: billing.Name,
            billingType: billing.BillingType,
            amount: billing.Amount.toString(),
            billingAmount: billing.Amount.toString(),
            soaNumber: billing.SOANumber,
            billingDate: billing.BillingDate,
            dueDate: billing.DueDate,
            description: billing.Description,
            status: "unpaid",
            createdAt: new Date().toISOString(),
            createdBy: currentUser.email,
            createdByName: currentUser.displayName,
          };

          // Add to Firebase
          await addNewDoc("billings", billingData);
          successCount++;
        } catch (error) {
          console.error("Error adding billing:", error);
          failedCount++;
        }
      }

      // Show success message
      if (successCount > 0) {
        toast.success(`Successfully imported ${successCount} billing records`);

        // Refresh billing list
        await getAllDocsSnap("billings", (docs) => {
          setBillings(docs);
        });
      }

      // Show error message if any failed
      if (failedCount > 0) {
        toast.error(`Failed to import ${failedCount} billing records`);
      }

      // Close modal and reset states
      setUploadMode(false);
      setCsvData([]);
      setValidationErrors([]);
    } catch (error) {
      console.error("Error during bulk import:", error);
      toast.error("An error occurred during import");
    } finally {
      setIsUploading(false);
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="text-xs max-w-7xl mx-auto mt-8">
      <div className="title-header">Billing and Collection</div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 my-6">
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-50 rounded-lg">
              <FiDollarSign className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Total Billings</div>
              <div className="text-2xl font-semibold text-gray-900">
                {stats.totalBillings}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-eleghoGreen/10 rounded-lg">
              <FiCheckCircle className="w-5 h-5 text-eleghoGreen" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Paid</div>
              <div className="text-2xl font-semibold text-eleghoGreen">
                {stats.paidBillings}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-eleghoBlue/10 rounded-lg">
              <FiClock className="w-5 h-5 text-eleghoBlue" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Pending</div>
              <div className="text-2xl font-semibold text-eleghoBlue">
                {stats.pendingBillings}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded-lg">
              <FiAlertCircle className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Overdue</div>
              <div className="text-2xl font-semibold text-red-500">
                {stats.overdueBillings}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <button
          className="px-4 py-2 bg-eleghoBlue text-white rounded-lg hover:bg-eleghoBlue/90 transition-colors text-xs inline-flex items-center gap-2"
          onClick={() => setAddMode(true)}
        >
          <FiPlusCircle className="w-4 h-4" />
          <span>Create new</span>
        </button>

        {/* <button className="px-4 py-2 bg-eleghoBlue text-white rounded-lg hover:bg-eleghoBlue/90 transition-colors text-xs inline-flex items-center gap-2">
          <FiCalendar className="w-4 h-4" />
          <span>Generate Annual Dues</span>
        </button>

        <button className="px-4 py-2 bg-eleghoBlue text-white rounded-lg hover:bg-eleghoBlue/90 transition-colors text-xs inline-flex items-center gap-2">
          <FiDroplet className="w-4 h-4" />
          <span>Generate Water Bills</span>
        </button>

        <button className="px-4 py-2 bg-eleghoBlue text-white rounded-lg hover:bg-eleghoBlue/90 transition-colors text-xs inline-flex items-center gap-2">
          <FiBell className="w-4 h-4" />
          <span>Send Reminder</span>
        </button> */}

        <button
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs inline-flex items-center gap-2"
          onClick={handleExcelDownload}
        >
          <FiDownload className="w-4 h-4" />
          <span>Download Excel</span>
        </button>

        <button
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-xs inline-flex items-center gap-2"
          onClick={handleTemplateDownload}
        >
          <FiFile className="w-4 h-4" />
          <span>Download CSV Template</span>
        </button>

        <button
          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs inline-flex items-center gap-2"
          onClick={triggerFileInput}
        >
          <FiUpload className="w-4 h-4" />
          <span>Upload Billings CSV</span>
        </button>

        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept=".csv"
          onChange={handleFileUpload}
          onClick={(e) => {
            // Reset the input value to ensure onChange fires even if the same file is selected
            e.target.value = null;
          }}
        />
      </div>

      {/* DataGrid Table */}
      <Paper sx={{ height: "70vh", width: "100%", marginTop: "1rem" }}>
        <DataGrid
          rows={billings}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
            sorting: {
              sortModel: [{ field: "dueDate", sort: "desc" }],
            },
          }}
          pageSizeOptions={[5, 10, 25]}
          sx={{
            border: 0,
            fontSize: "0.8rem",
            fontFamily: "inherit",
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
          }}
          onRowClick={handleViewMode}
        />
      </Paper>

      {addMode && (
        <PageModal>
          <BillingForm setAddMode={setAddMode} />
        </PageModal>
      )}

      {viewMode && (
        <PageModal>
          <BillingPageDetails
            data={selectedForView}
            setViewMode={setViewMode}
          />
        </PageModal>
      )}

      {uploadMode && (
        <PageModal>
          <div className="page relative bg-white rounded-lg shadow-lg p-6 max-w-5xl mx-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-[#363062]">
                CSV Import Preview
              </h2>
              <button
                onClick={() => setUploadMode(false)}
                className="text-gray-500 hover:text-[#363062] transition-colors"
              >
                <FiXCircle size={20} />
              </button>
            </div>

            {validationErrors.length > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <h3 className="text-red-600 font-medium mb-2">
                  Validation Errors
                </h3>
                <ul className="list-disc pl-5 text-sm text-red-600">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mb-4">
              <p className="text-gray-700">
                Found {csvData.length} valid billing records to import. Review
                the data below before confirming.
              </p>
            </div>

            <div className="overflow-x-auto max-h-96 overflow-y-auto mb-6">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    {csvData.length > 0 &&
                      Object.keys(csvData[0]).map((header) => (
                        <th
                          key={header}
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {header}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {csvData.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className={rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      {Object.values(row).map((value, cellIndex) => (
                        <td
                          key={cellIndex}
                          className="px-3 py-2 whitespace-nowrap text-xs text-gray-700"
                        >
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setUploadMode(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-xs"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveBillings}
                disabled={isUploading || csvData.length === 0}
                className={`px-4 py-2 rounded-lg text-white text-xs flex items-center ${
                  isUploading || csvData.length === 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {isUploading ? (
                  <>
                    <span className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full"></span>
                    <span>Importing...</span>
                  </>
                ) : (
                  <>
                    <FiUpload className="w-4 h-4 mr-2" />
                    <span>Import Billings</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </PageModal>
      )}
    </div>
  );
}
